import { ref } from "vue";

/**
 * @typedef {object} ConfirmOptions
 * @property {string} [title] Dialog heading.
 * @property {string | (() => import("vue").VNode)} [description] Question shown under the heading.
 *   Pass a render function when the text needs markup: `() => h("span", ["Text ", h("strong", name)])`.
 * @property {string} [confirmLabel] Confirm button label. Defaults to "Підтвердити".
 * @property {string} [cancelLabel] Cancel button label. Defaults to "Скасувати".
 * @property {"primary"|"destructive"} [confirmStyle]
 *   Confirm button style. Defaults to "primary"; use "destructive" for irreversible actions.
 */

const isOpen = ref(false);
const options = ref({});

let resolveCurrent = null;

function settle(isConfirmed) {
  const resolve = resolveCurrent;

  resolveCurrent = null;
  isOpen.value = false;
  resolve?.(isConfirmed);
}

/**
 * The application-wide confirmation dialog. Its markup lives once in `App.vue`;
 * this composable holds the state and the user's answer.
 *
 * `isOpen`, `options`, `accept` and `cancel` are meant for that host only —
 * call sites need `confirm` alone.
 * @example
 * const confirmDialog = useConfirm();
 *
 * const isConfirmed = await confirmDialog.confirm({
 *   title: "Видалити оплату",
 *   description: "Ви впевнені, що хочете видалити оплату?",
 *   confirmLabel: "Видалити",
 *   confirmStyle: "destructive",
 * });
 *
 * if (!isConfirmed) {
 *   return;
 * }
 */
export function useConfirm() {
  /**
   * Asks the user to confirm an action.
   * @param {ConfirmOptions} [confirmOptions]
   * @returns {Promise<boolean>} `true` when the user confirmed, `false` on any other way of closing.
   */
  function confirm(confirmOptions = {}) {
    settle(false);

    options.value = confirmOptions;

    return new Promise((resolve) => {
      resolveCurrent = resolve;
      isOpen.value = true;
    });
  }

  return {
    confirm,
    isOpen,
    options,
    accept: () => settle(true),
    cancel: () => settle(false),
  };
}
